/**
 * Draft › Order and lottery — the page shell.
 *
 * Built from `FFL Pass 05 Draft Order.dc.html` (1a phone, 1d reveal, 1e desktop, 1f commissioner)
 * against `draft-order-brief.md`. The ids skip b and c: two options were explored and cut, and
 * nothing is missing.
 *
 * THE ONE SENTENCE. This screen used to spend 142 of its 505 words explaining the lottery, in three
 * numbered cards. §1 replaces all three with the sentence below, which appears on the phone, on the
 * desktop, and nowhere else. Both halves of it are load-bearing — the first states the
 * counterintuitive fact and the second is what makes it actionable — so it does not get shortened,
 * and the cards do not come back. Everything else went to the rulebook behind `§` links.
 *
 * THE REVEAL TAKES THE PAGE. `draftState.lotteryDrawnAt` is the broadcast: the moment the
 * commissioner records a draw, every subscribed screen swaps to 1d and starts the same ten rows
 * filling from the same origin. It holds on the finished board until the GM leaves it (Tim, 21
 * August) rather than dropping him back mid-thought.
 */

import { useEffect, useMemo, useState } from 'react';
import { AnchorMark, Linkify } from './RuleText.js';
import { managerShort } from '../domain/identity.js';
import { MyDraftSlot } from './DraftPrefs.js';
import { LotteryRunner } from './LotteryRunner.js';
import { ThirdPlaceSwap } from './ThirdPlaceSwap.js';
import { type SwapDeclaration, type SwapInput } from '../domain/thirdPlaceSwap.js';
import { drawFor, placedByRule, rankingsForResolve, seededRng } from '../domain/draftOrder.js';
import {
  columnHeads, ledgerRows, orderPhase, ranksLine, resolutionChecks, slotTrail,
} from '../domain/orderLedger.js';
import { OrderLedger } from './OrderLedger.js';
import { swapState } from '../domain/thirdPlaceSwap.js';
import type { SwapState } from '../domain/thirdPlaceSwap.js';
import type { ResolutionSnapshot } from '../domain/publication.js';
import { LotteryReveal } from './LotteryReveal.js';
import { LOTTERY_BASIS } from '../domain/season2025.js';
import { bothZones, clockIn, gateOf, publicationDue, timeLeft, ZONES } from '../domain/deadlines.js';
import { ordinal, revealDurationMs, revealTakesPage } from '../domain/lotteryReveal.js';
import { rankingCensus } from '../domain/rankingCensus.js';
import {
  atLeast, type DraftPreference, type DraftState, type LeagueSettings, type LeagueStore, type Role,
} from '../store/types.js';

const SEASON = 2026;
const TEAMS = 10;
/** Guards the snapshot's stored string, which is `unknown` by the time it reaches this screen. */
const SWAP_STATES: readonly string[] = ['pending', 'taken', 'declined', 'lapsed'];

/*
 * THE BASIS, NOT THE ORDER OF FINISH. "Worst finisher first" means the table the rules rank last
 * season by, and since Pass 06 §1 that is the regular season below the playoff places — so the
 * chalk projection and the reveal's weights both read `LOTTERY_BASIS`. `season2025.ts` is the one
 * place the two orderings are combined.
 */

/**
 * §1, verbatim, and the only explanation on the screen. Exported so the acceptance test can assert
 * it is one sentence pair in one place rather than a paraphrase that drifted.
 */
export const EXPLAINER =
  'The lottery sets the order GMs choose in, not the slots themselves. When your turn comes you '
  + 'get the highest slot still free on your list.';

export function DraftPosition(
  { gm, role, store, only }:
  { gm: string; role: Role; store: LeagueStore; only?: 'me' | 'run' },
) {
  const [settings, setSettings] = useState<LeagueSettings | null>(null);
  const [draft, setDraft] = useState<DraftState | null>(null);
  const [prefs, setPrefs] = useState<DraftPreference[]>([]);
  const [pane, setPane] = useState<'me' | 'run'>(only ?? 'me');
  /**
   * WHETHER THE PREFERENCES HAVE ARRIVED, not whether there are any.
   *
   * The submitted marks are read off an empty array identically to a league where nobody has
   * ranked — so without this the ledger opens with ten hollow rings and settles a beat later,
   * which is the screen telling every GM his ranking is missing and then taking it back.
   */
  const [loaded, setLoaded] = useState(false);
  /**
   * Keyed by the draw it dismissed, so a re-run puts the board back up.
   *
   * PERSISTED, because component state does not survive the reload that follows every deploy —
   * and a dismissal that forgets itself is the same as no dismissal at all. Per browser, which is
   * the right scope: "have I watched this" is a fact about the person at the screen.
   */
  const [dismissed, setDismissed] = useState<string | null>(() => readSeen());
  /** He asked for it back. Nothing to do with whether it opens itself. */
  const [replay, setReplay] = useState(false);

  const isCommish = atLeast(role, 'commissioner');
  const canRun = isCommish && only !== 'me';

  useEffect(() => {
    let live = true;
    const load = () => Promise.all([
      store.settings(SEASON), store.draftState(SEASON), store.preferences(SEASON),
    ]).then(([s, d, p]) => {
      if (live) { setSettings(s); setDraft(d); setPrefs(p); setLoaded(true); }
    });
    void load();
    const off = store.subscribe(SEASON, (what) => {
      if (what === 'settings' || what === 'draftState' || what === 'preferences') void load();
    });
    return () => { live = false; off(); };
  }, [store]);

  const gate = gateOf(settings, 'preferences', Date.now());
  const order = useMemo(() => draft?.selectionOrder ?? [], [draft]);
  const published = Boolean(draft?.published) && draft?.order?.length === TEAMS;
  /*
   * ONE CENSUS, and the strip below and Home now read the same field off it. The strip used to
   * count `submittedAt` and Home counted documents, so the app gave two different answers to
   * "how many rankings are in" on the same afternoon. See `domain/rankingCensus.ts` for why the
   * headline is `willResolve` and not either of the old numbers.
   */
  const census = useMemo(() => rankingCensus(prefs, TEAMS), [prefs]);
  /** 8:00 PM ET on the day rankings are due — §7's promise, off §5's date. */
  const publish = publicationDue(settings?.preferenceDeadline);
  const finishOrder = draft?.finishOrder?.length === TEAMS ? draft.finishOrder : LOTTERY_BASIS;

  /*
   * THE LEDGER'S INPUTS, assembled once.
   *
   * Everything on this page that varies by phase reads from `domain/orderLedger.ts`, and the
   * whole reason that module exists is that these four documents — the draw, the rankings, the
   * resolution and the third-place declaration — were being re-derived slightly differently by
   * every card that wanted them. See the note at the top of it on why two-way flags fall through.
   */
  const snapshot = draft?.resolution as ResolutionSnapshot | undefined;
  const prefsByGm = useMemo(
    () => Object.fromEntries(prefs.map((p) => [p.gm.toUpperCase(), p.ranked])),
    [prefs],
  );
  /**
   * GM → when the league recorded his ranking, for the ledger's receipt column.
   *
   * THE STAMP, NOT A BOOLEAN. `hasSubmitted` is derived from the presence of `submittedAt`, so
   * carrying the instant costs nothing and lets the tick say when — which is the half of the
   * answer that settles an argument about whether something saved.
   */
  const stampsByGm = useMemo<Record<string, string | null>>(
    () => Object.fromEntries(prefs.map((p) => [p.gm.toUpperCase(), p.submittedAt ?? null])),
    [prefs],
  );
  const slotsByGm = useMemo<Record<string, number>>(() => {
    /* The published board IS the answer: index 0 is slot 1. */
    if (published) {
      return Object.fromEntries(draft!.order.map((g, i) => [g.toUpperCase(), i + 1]));
    }
    /* Held: the resolve has run and only the snapshot has the answer. */
    if (snapshot?.slots?.length) {
      return Object.fromEntries(snapshot.slots.map((x) => [x.gm.toUpperCase(), x.slot]));
    }
    return {};
  }, [published, draft, snapshot]);

  const top4 = useMemo(() => draft?.lotteryTop4
    ?? (draft?.lotterySeed != null
      ? drawFor(finishOrder, seededRng(draft.lotterySeed), draft?.toiletBowlWinner ?? null).top4
      : undefined), [draft, finishOrder]);
  /* Lifted to position 2 by 2020-II-1 §3 rather than drawn there — see `placedByRule`. */
  const liftedByRule = draft?.toiletBowlWinner
    && (top4 ? placedByRule(top4, draft.toiletBowlWinner) : true)
    ? draft.toiletBowlWinner : null;

  /*
   * THE BROADCAST. One stored instant, read by every screen. A GM who arrives eight seconds late
   * joins the draw in progress; one who arrives an hour later gets the finished board, which is
   * still the right thing to show him, and leaves it when he has read it.
   */
  const drawnAt = draft?.lotteryDrawnAt ?? null;
  /*
   * THE BROADCAST HAS AN END (`revealTakesPage`). It used to be "there is a draw and you have not
   * dismissed it in this component", which meant a draw recorded on the 31st with the 26th's
   * timestamp replayed itself full-screen on every load, for everybody, indefinitely.
   */
  const showReveal = (replay || revealTakesPage({
    drawnAt,
    complete: order.length === TEAMS,
    seen: dismissed === drawnAt,
    now: Date.now(),
  })) && order.length === TEAMS && Boolean(drawnAt);
  /** Once it has stopped opening itself, there has to be a way back in. */
  const canReplay = Boolean(drawnAt) && order.length === TEAMS && !showReveal;

  /*
   * THE THIRD-PLACE OPTION (Pass 06 §3). Everything it needs is already on this page — the draw,
   * the two men, and the preference deadline it expires at — so it is assembled once here rather
   * than re-derived inside the card.
   */
  const swap: SwapInput = useMemo(() => ({
    thirdPlace: draft?.thirdPlace ?? null,
    fourthPlace: draft?.fourthPlace ?? null,
    selectionOrder: order,
    drawnAt,
    deadline: settings?.preferenceDeadline ?? null,
    declaration: draft?.thirdFourthDeclaration ?? null,
  }), [draft, order, drawnAt, settings?.preferenceDeadline]);

  /**
   * BINDING, so it is written once and not toggled.
   *
   * The write carries the whole declaration rather than a boolean, because September's argument
   * will be about who said what and when — and a commissioner recording a verbal answer has to
   * leave a record as good as a tap. The old `thirdFourthSwap` boolean is written alongside it so
   * a screen that has not been rebuilt yet still resolves the draft the same way.
   */
  const declare = async (d: SwapDeclaration) => {
    if (!draft) return;
    await store.saveDraftState({
      ...draft,
      thirdFourthDeclaration: d,
      thirdFourthSwap: d.answer === 'taken',
      updatedAt: new Date().toISOString(),
    });
    setDraft({ ...draft, thirdFourthDeclaration: d, thirdFourthSwap: d.answer === 'taken' });
  };

  /*
   * WHICH OF THE SIX. `launching` is the commissioner standing in front of the runner rather than
   * a state of the season, which is why it is a view flag and not a stored one.
   */
  const phase = orderPhase({
    drawnAt,
    drawn: order.length === TEAMS ? TEAMS : 0,
    teams: TEAMS,
    launching: canRun && (only ?? pane) === 'run',
    resolvedAt: draft?.resolvedAt ?? null,
    publishedAt: draft?.publishedAt ?? (published ? draft?.updatedAt ?? null : null),
  });

  /*
   * THE OPTION'S STATE, and where it is read from changes once the resolve has run.
   *
   * `swapState` derives `lapsed` from the clock, which is right up to the moment the resolve
   * walks the board — after that the answer is a fact about what the resolve did, and it is
   * frozen in the snapshot beside the slots it produced. Re-deriving it against `Date.now()` on a
   * published board is asking a question that was settled on 31 August, and the answer it gives
   * on a machine whose deadline is set differently is not the one the league lived through.
   */
  const frozenSwap = SWAP_STATES.includes(snapshot?.swap?.state as SwapState)
    ? snapshot!.swap!.state as SwapState : null;
  const option = draft?.thirdPlace && draft?.fourthPlace
    ? {
      third: draft.thirdPlace,
      fourth: draft.fourthPlace,
      state: (phase === 'held' || phase === 'published') && frozenSwap
        ? frozenSwap : swapState(swap),
    }
    : null;

  const rows = ledgerRows({
    phase,
    basis: finishOrder,
    order,
    drawn: order.length === TEAMS ? TEAMS : 0,
    teams: TEAMS,
    me: gm,
    slots: slotsByGm,
    prefs: prefsByGm,
    submitted: loaded ? stampsByGm : undefined,
    rankings: loaded ? census : undefined,
    placedByRule: liftedByRule,
    option,
  });
  const heads = columnHeads({
    phase,
    drawn: order.length === TEAMS ? TEAMS : 0,
    teams: TEAMS,
    settledOn: drawnAt ? dayLabel(drawnAt) : null,
    publishedOn: draft?.publishedAt ? dayLabel(draft.publishedAt) : null,
  });

  const checks = phase === 'held' ? resolutionChecks({
    teams: TEAMS,
    /* Every list the slots will be worked out from — submitted only, which is what the resolve
       reads. A saved-and-unsent list is counted here as unranked, because that is how it lands. */
    ranked: census.willResolve,
    slotsComplete: Object.keys(slotsByGm).length === TEAMS,
    toiletBowlWinner: draft?.toiletBowlWinner ?? null,
    toiletBowlPosition: draft?.toiletBowlWinner
      ? order.findIndex((g) => g.toUpperCase() === draft.toiletBowlWinner!.toUpperCase()) + 1
      : null,
    option: option ? {
      ...option,
      thirdPosition: order.findIndex((g) => g.toUpperCase() === option.third.toUpperCase()) + 1,
      fourthPosition: order.findIndex((g) => g.toUpperCase() === option.fourth.toUpperCase()) + 1,
    } : null,
    /* Who §6 actually takes over for: no list, OR a list he never sent. */
    unranked: order.filter((g) => !(rankingsForResolve(prefs)[g.toUpperCase()] ?? []).length),
    name: managerShort,
  }) : [];

  const trail = phase === 'published' ? slotTrail({
    order,
    slots: slotsByGm,
    prefs: prefsByGm,
    me: gm,
    placedByRule: liftedByRule,
    name: managerShort,
  }) : [];

  /** Where the viewer chooses, once the draw has said. Zero while it has not. */
  const myTurn = order.findIndex((g) => g.toUpperCase() === gm.toUpperCase()) + 1;
  const myRanks = prefs.find((p) => p.gm.toUpperCase() === gm.toUpperCase())?.ranked ?? [];
  const ranks = myTurn > 0 ? ranksLine(myTurn, TEAMS, myRanks.length) : null;

  if (showReveal && drawnAt) {
    return (
      <div className="app dolapp dolrevealpage">
        <LotteryReveal
          selectionOrder={order}
          /*
           * Stored with the draw from 27 August. A draw recorded before that has none, and the
           * seed recovers it — the same computation `selectionOrder` ran, from the number it
           * recorded (2014-II-1 §5).
           */
          top4={draft?.lotteryTop4
            ?? (draft?.lotterySeed != null
              ? drawFor(finishOrder, seededRng(draft.lotterySeed),
                draft?.toiletBowlWinner ?? null).top4
              : undefined)}
          finishOrder={finishOrder}
          me={gm}
          startedAt={drawnAt}
          teams={TEAMS}
          toiletBowlWinner={draft?.toiletBowlWinner ?? null}
          thirdPlace={draft?.thirdPlace ?? null}
          fourthPlace={draft?.fourthPlace ?? null}
          onDismiss={() => { setReplay(false); setDismissed(drawnAt); writeSeen(drawnAt); }}
        />
      </div>
    );
  }

  const lotteryState = published ? 'Published'
    : order.length === TEAMS ? 'Drawn — not published'
      : 'Not run yet';

  return (
    <div className="app dolapp">
      <Linkify>
        <header className="dolhead">
          <div className="dolheadline">
            <h1>{canRun ? 'Draft order and lottery' : 'Your draft slot'}</h1>
            <AnchorMark anchor="lottery-how-it-works" />
          </div>
          {/* §1. One sentence, both halves, on both breakpoints, and nowhere else. */}
          <p className="dolsay">{EXPLAINER}</p>
        </header>

        {/*
          Six cards became a strip: the three facts that decide whether anything on this page is
          urgent, and the promise about when the answer arrives (§4). It is a desktop element and
          the stylesheet hides it under 900 — on a phone the counter card carries the only status
          that matters there.
        */}
        <div className="dolstrip">
          <div className="dolstripitem">
            <span className="dolcaps">Lottery</span>
            <span className="dolstripval">{lotteryState}</span>
          </div>
          <span className="dolstriprule" aria-hidden="true" />
          <div className="dolstripitem">
            <span className="dolcaps">Rankings due</span>
            <span className={`dolstripval${gate.open ? ' due' : ''}`}>
              {settings?.preferenceDeadline
                ? `${bothZones(settings.preferenceDeadline)} · ${timeLeft(settings.preferenceDeadline)}`
                : 'Not set yet'}
            </span>
          </div>
          <span className="dolstriprule" aria-hidden="true" />
          {/*
            "RANKINGS IN" is the men whose own list will decide their slot, which is the men who
            sent one. A saved-but-unsent list is on the tooltip rather than in the figure: it is
            worth a commissioner knowing about — that man is one tap from the outcome he wants —
            and counting it told him he had nothing to do about it, which was the opposite of true.
          */}
          <div className="dolstripitem" title={census.saved > 0
            ? `${census.sent} sent · ${census.saved} saved but never sent, which the resolve `
              + 'will not read'
            : undefined}>
            <span className="dolcaps">Rankings in</span>
            <span className="dolstripval">{census.count}</span>
          </div>
          {/*
            DERIVED, NEVER LITERAL (Pass 06 §6). It read "by 8:00 PM the same evening", which is
            true and gives a GM nothing he can put in a calendar — "the same evening" as what?
            2020-II-1 §5 sets rankings at 5:00 PM and §7 publication at 8:00 PM that evening, so
            the date follows whatever the commissioners actually set and moves when they move it.

            ET on the face, MT in the tooltip: the strip is one line and a second clock inline
            turns it into a paragraph.
          */}
          <span className="dolstrippromise" title={publish ? bothZones(publish) : undefined}>
            {publish
              ? `Resolved order published by ${clockIn(publish, ZONES[0]!.tz)} `
                + `${ZONES[0]!.label} on ${shortDay(publish)}`
              : 'Resolved order published the evening rankings are due'}
          </span>
        </div>

        {/*
          THE LEAD CARD FOLLOWS THE PHASE, and there is exactly one. Each of these is the single
          thing a GM can act on in that state — which is why `held` has none: the answer exists,
          it is nobody's yet, and there is nothing for him to do but wait for the release.

          ABOVE THE LEDGER, and that is a phone decision that costs the desktop nothing. Ten rows
          is most of a 390px screen, and the figure the handoff calls "the only number that changes
          what a GM does next" was arriving underneath all of them.
        */}
        {phase === 'pre' && !published && (
          <section className="dolcard dollead">
            <span className="dolcaps">Optional, for now</span>
            <h2>Rank now, or wait until you know your turn</h2>
            <p>
              Nothing&rsquo;s due until{' '}
              {settings?.preferenceDeadline ? bothZones(settings.preferenceDeadline) : '5pm on 31 August'},
              and you can change your ranking after the draw.
            </p>
          </section>
        )}

        {phase === 'after' && ranks && (
          /*
            THE NUMBER IS THE SCREEN on a phone — 44px, the largest thing on it — because it is the
            only figure here that changes what a GM does next. Derived from his own turn, never a
            literal: a hard-coded six drifts from the ledger the moment anybody re-runs the draw.
          */
          <section className="dolcard dollead dolranks">
            <span className="dolranksnum">{ranks.need}</span>
            <div className="dolrankssay">
              <h2>{ranks.title}</h2>
              <p>{ranks.said}</p>
            </div>
          </section>
        )}

        {/*
          THE LEDGER, in every state — `design_handoff_order_and_lottery`.

          It replaced a card that only existed after publication, which is exactly the shape the
          handoff is arguing against: the page's job is to hold two answers that arrive five days
          apart, and a table that appears only once both are in cannot show anybody where he is in
          between. Same three columns all six times; only how much of it is filled changes.
        */}
        <OrderLedger heads={heads} rows={rows} />

        {canReplay && (
          <p className="dolreplay">
            <button type="button" className="linkbtn" onClick={() => setReplay(true)}>
              Replay the draw
            </button>
          </p>
        )}

        {phase === 'held' && (
          <section className="dolcard dolheld">
            <span className="dolcaps">Resolved, not yet released</span>
            <h2>Your slot exists and is nobody&rsquo;s to see yet</h2>
            <ul className="dolchecks">
              {checks.map((c) => (
                <li key={c.said} className={c.state}>
                  <span className="dolcheckstate">{c.state === 'note' ? 'check' : c.state}</span>
                  <span>{c.said}</span>
                </li>
              ))}
            </ul>
            {/*
              PLAIN, NOT CLEVER. This read "They're checking the inputs, not the maths" — a turn
              of phrase, in British spelling, that a manager has to stop and unpack. It is saying
              one simple thing, so it says it (Tim, 31 August).
            */}
            <p className="dolcheckfoot">
              The commissioners are checking that every ranking arrived. They cannot change which
              slot the rules give you.
            </p>
          </section>
        )}

        {phase === 'published' && trail.length > 0 && (
          /*
            WHAT OTHER LISTS DID, NEVER WHAT THEY WERE. Every GM sees this for his own slot; whose
            ranking blocked whom is not the reader's business. See `slotTrail`.
          */
          <section className="dolcard doltrail">
            <span className="dolcaps">
              How you got {slotsByGm[gm.toUpperCase()]}
              {slotsByGm[gm.toUpperCase()] ? ordinalOf(slotsByGm[gm.toUpperCase()]!) : ''}
            </span>
            <ol>
              {trail.map((t) => (
                <li key={t.gm} className={t.mine ? 'mine' : ''}>{t.said}</li>
              ))}
            </ol>
          </section>
        )}

        {canRun && (
          <div className="dolpanes" role="tablist" aria-label="Draft position view">
            <button type="button" role="tab" aria-selected={pane === 'me'}
              className={`dolpane${pane === 'me' ? ' on' : ''}`} onClick={() => setPane('me')}>
              My ranking
            </button>
            <button type="button" role="tab" aria-selected={pane === 'run'}
              className={`dolpane${pane === 'run' ? ' on' : ''}`} onClick={() => setPane('run')}>
              Run the lottery
            </button>
          </div>
        )}

        {(only ?? pane) === 'me' ? (
          <>
            {/*
              Above the ranking, because it changes what the ranking has to say: 4th's required
              depth moves while this is pending, and 3rd's whole position can change with one tap.
            */}
            <ThirdPlaceSwap
              swap={swap}
              me={gm}
              canRecord={isCommish}
              actor={gm}
              deadline={settings?.preferenceDeadline ?? null}
              onDeclare={declare}
            />
            <MyDraftSlot gm={gm} store={store} draft={draft} gate={gate} canOverride={isCommish}
              /* The census's own figure, so this card cannot say 8 while the strip says 9. */
              sealed={phase === 'pre' || phase === 'launch'
                ? null : { ranked: census.willResolve }}
              need={ranks?.need ?? null}
              roll={prefs.map((p) => ({ gm: p.gm, submittedAt: p.submittedAt ?? null, updatedAt: p.updatedAt ?? null }))}
              order={order.length ? order : finishOrder}
              published={phase === 'published'} />
          </>
        ) : (
          <LotteryRunner gm={gm} store={store} />
        )}
      </Linkify>
    </div>
  );
}

/**
 * WHICH DRAW THIS BROWSER HAS WATCHED. One key, holding the `lotteryDrawnAt` it was dismissed at,
 * so a re-run — a different instant — puts the board back up on its own.
 *
 * Wrapped, because storage throws outright in a few contexts (private windows with site data
 * blocked, thumbnailers) and a lottery page that cannot render because it could not read a
 * convenience is a much worse bug than one that replays a draw.
 */
const SEEN_KEY = 'ffl.lottery.seen';

function readSeen(): string | null {
  try { return window.localStorage.getItem(SEEN_KEY); } catch { return null; }
}

function writeSeen(at: string | null): void {
  try { if (at) window.localStorage.setItem(SEEN_KEY, at); } catch { /* not worth a crash */ }
}

/** Exported for the acceptance test: how long a reveal runs, so nothing has to guess. */
export const REVEAL_MS = revealDurationMs(TEAMS);

/** "26 August" — the ledger's own sub-line, which has no room for a weekday. */
const dayLabel = (iso: string) => new Date(iso).toLocaleDateString('en-GB', {
  month: 'long', day: 'numeric', timeZone: 'America/New_York',
});

const ordinalOf = (n: number) => ordinal(n);

/** "Mon, Aug 31" in league time — the strip has already said the clock. */
const shortDay = (iso: string) => new Date(iso).toLocaleDateString('en-US', {
  weekday: 'short', month: 'short', day: 'numeric', timeZone: 'America/New_York',
});
